import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Table, Spinner } from 'react-bootstrap';
import { FaUsers, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Header from './common/header';
import apiService from '../services/apiService';

const ManageUsersPage = () => {
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);
    const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
    const [busca, setBusca] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingAcesso, setLoadingAcesso] = useState({}); // { email: true/false }
    const [modernAlert, setModernAlert] = useState({ show: false, type: '', message: '' });

    const showModernAlert = (message, type = 'success') => {
        setModernAlert({ show: true, type, message });
        setTimeout(() => {
            setModernAlert({ show: false, type: '', message: '' });
        }, 5000);
    };

    useEffect(() => {
        carregarUsuarios();
    }, []);

    useEffect(() => {
        if (busca.trim() === '') {
            setUsuariosFiltrados(usuarios);
        } else {
            const filtrados = usuarios.filter(user =>
                user.nome.toLowerCase().includes(busca.toLowerCase()) ||
                user.email.toLowerCase().includes(busca.toLowerCase())
            );
            setUsuariosFiltrados(filtrados);
        }
    }, [busca, usuarios]);

    const carregarUsuarios = async () => {
        try {
            setLoading(true);
            const response = await apiService.listarUsuarios();
            setUsuarios(response.usuarios || []);
            setUsuariosFiltrados(response.usuarios || []);
        } catch (error) {
            showModernAlert('Erro ao carregar usuários: ' + (error.message || ''), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAcessoChange = async (email, novoAcesso) => {
        try {
            // Marcar este usuário como loading
            setLoadingAcesso({ ...loadingAcesso, [email]: true });

            const response = await apiService.atualizarAcesso(email, novoAcesso);

            if (response.success) {
                setUsuarios(usuarios.map(user =>
                    user.email === email ? { ...user, acesso: novoAcesso } : user
                ));
                showModernAlert('Acesso atualizado com sucesso!', 'success');
            }
        } catch (error) {
            showModernAlert('Erro ao atualizar acesso: ' + (error.message || ''), 'error');
            carregarUsuarios();
        } finally {
            // Remover loading deste usuário
            setLoadingAcesso(prev => {
                const newState = { ...prev };
                delete newState[email];
                return newState;
            });
        }
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden">
            <Header />

            <style>
                {`
                    .no-hover-table tbody tr:hover {
                    }
                    .no-hover-table tbody tr:hover td {
                        border-color: #dee2e6 !important;
                    }
                    .no-hover-table thead tr:hover {
                    }
                    .no-hover-table thead tr:hover th {
                        border-color: #dee2e6 !important;
                    }
                `}
            </style>

            {modernAlert.show && (
                <div
                    style={{
                        position: 'fixed',
                        top: '2rem',
                        right: '2rem',
                        zIndex: 9999,
                        maxWidth: '400px',
                        background: modernAlert.type === 'success' ? '#003B6B' : '#dc3545',
                        color: 'white',
                        padding: '1rem 1.5rem',
                        borderRadius: '1rem',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        border: 'none',
                        fontWeight: '600',
                        fontSize: '0.95rem',
                        animation: 'slideInRight 0.5s ease-out',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                    }}
                >
                    <div style={{
                        width: '2rem',
                        height: '2rem',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        {modernAlert.type === 'success' ? '✓' : '✕'}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '700', marginBottom: '0.25rem' }}>
                            {modernAlert.type === 'success' ? 'Sucesso!' : 'Erro!'}
                        </div>
                        <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>
                            {modernAlert.message}
                        </div>
                    </div>
                    <button
                        onClick={() => setModernAlert({ show: false, type: '', message: '' })}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            borderRadius: '0.25rem',
                            opacity: 0.7,
                            fontSize: '1.2rem',
                            lineHeight: 1,
                            transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = 1}
                        onMouseLeave={(e) => e.target.style.opacity = 0.7}
                    >
                        ×
                    </button>
                </div>
            )}

            <Container fluid className="flex-1 bg-gray-50 overflow-auto" style={{ backgroundColor: '#f8f9fa', paddingTop: '40px', paddingBottom: '40px' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    {/* Botão Voltar no topo */}
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Button
                            variant="primary"
                            onClick={() => navigate('/')}
                            style={{
                                backgroundColor: '#003B6B',
                                borderColor: '#003B6B',
                                padding: '10px 40px',
                                fontWeight: 'bold',
                                borderRadius: '8px'
                            }}
                        >
                            Voltar
                        </Button>
                    </div>

                    {/* Card Gerenciar permissões de acesso */}
                    <div style={{ marginBottom: '20px', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <div
                            className="bg-gradient-to-r from-slate-700 to-slate-600"
                            style={{
                                padding: '15px 20px',
                                color: 'white'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <FaUsers size={20} />
                                <h5 style={{ fontWeight: 'bold', margin: 0 }}>Gerenciar permissões de acesso</h5>
                            </div>
                        </div>

                        <div style={{ backgroundColor: 'white', padding: '40px' }}>
                        {/* Campo de busca */}
                        <div style={{ marginBottom: '20px', position: 'relative' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                <FaSearch />
                                <Form.Label style={{ fontWeight: 'bold', margin: 0 }}>Buscar</Form.Label>
                            </div>
                            <Form.Control
                                type="text"
                                placeholder="Pesquisar por nome ou email..."
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                style={{
                                    padding: '10px',
                                    borderRadius: '4px'
                                }}
                            />
                        </div>

                        {/* Tabela de usuários */}
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Carregando...</span>
                                </div>
                            </div>
                        ) : (
                            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                <Table striped bordered className="no-hover-table">
                                    <thead>
                                        <tr>
                                            <th style={{ fontWeight: 'bold',  textAlign: 'center' }}>NOME</th>
                                            <th style={{ fontWeight: 'bold',  textAlign: 'center' }}>EMAIL</th>
                                            <th style={{ fontWeight: 'bold',  textAlign: 'center' }}>ACESSO</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {usuariosFiltrados.length === 0 ? (
                                            <tr>
                                                <td colSpan="3" style={{ textAlign: 'center', padding: '20px' }}>
                                                    Nenhum usuário encontrado
                                                </td>
                                            </tr>
                                        ) : (
                                            usuariosFiltrados.map((user, index) => (
                                                <tr key={index}>
                                                    <td style={{
                                                        textAlign: 'center',
                                                        padding: '12px',
                                                        borderRight: '1px solid #dee2e6'
                                                    }}>
                                                        {user.nome}
                                                    </td>
                                                    <td style={{
                                                        textAlign: 'center',
                                                        padding: '12px',
                                                        borderRight: '1px solid #dee2e6'
                                                    }}>
                                                        {user.email}
                                                    </td>
                                                    <td style={{
                                                        textAlign: 'center',
                                                        padding: '12px'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '10px'
                                                        }}>
                                                            <Form.Select
                                                                value={user.acesso}
                                                                onChange={(e) => handleAcessoChange(user.email, e.target.value)}
                                                                disabled={loadingAcesso[user.email]}
                                                                style={{
                                                                    fontWeight: 'bold',
                                                                    color: user.acesso === 'Não verificado' ? '#6c757d' : '#000',
                                                                    textAlign: 'center',
                                                                    maxWidth: '200px',
                                                                    opacity: loadingAcesso[user.email] ? 0.6 : 1
                                                                }}
                                                            >
                                                                <option value="Não verificado">NÃO VERIFICADO</option>
                                                                <option value="Verificado">VERIFICADO</option>
                                                                <option value="Administrador">ADMINISTRADOR</option>
                                                            </Form.Select>
                                                            {loadingAcesso[user.email] && (
                                                                <Spinner animation="border" size="sm" variant="primary" />
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default ManageUsersPage;
